'use client';

import { useEffect, useRef, useState } from 'react';

import LoadingPage from '../../Member/LoadingPage';
import { useAppLoggedInStore } from '../../store/appLoggedInStore';
import { useTokenStore } from '../../store/tokenStore';
import { isStageDev } from '../../utils/common';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import { useHistoryMethod } from '../../utils/hook/useHistoryMethod';
import { usePathnameMethod } from '../../utils/hook/usePathnameMethod';
import { useOnLoginSuccess } from '../../utils/hook/users';
import { DatadogLogPolicy } from '../../utils/policy/DatadogLogPolicy';
import { isReactNative } from '../../utils/react-native';

const COMMON_DATADOG_CONFIG = {
  clientToken: 'pub3ecf1970a908756825599b76bab300f2',
  site: 'datadoghq.com',
  service: 'uglyus-web-nextjs',
  env: isStageDev ? 'development' : 'production',
  /**
   * preview.yml 과 일치해야 함
   */
  version: '0.0.2',
} as const;

/**
 * RUM 세션 수집률(%). Datadog RUM Measure/Investigate 비용과 직결되며,
 * 실사용이 커밋 볼륨을 초과해 100 → 70으로 하향(비용 절감안).
 * Logs에는 적용하지 않고 RUM init에만 적용해 로그 수집은 현행(100%) 유지한다.
 */
const RUM_SESSION_SAMPLE_RATE = 70;

/**
 * Session Replay 녹화율(%). sessionSampleRate로 이미 샘플된 세션 중 비율이라
 * 실효 녹화율 = RUM_SESSION_SAMPLE_RATE × 이 값. Session Replay 비용 절감을 위해
 * 100 → 50으로 하향(실효 녹화율 70% × 50% = 35%).
 */
const RUM_SESSION_REPLAY_SAMPLE_RATE = 50;

/**
 * 봇 트래픽 필터링을 위한 패턴
 * 참고: https://docs.datadoghq.com/real_user_monitoring/guide/identify-bots-in-the-ui/
 */
const BOT_PATTERNS = [
  'bot',
  'crawler',
  'spider',
  'googlebot',
  'bingbot',
  'yandex',
  'baidu',
  'duckduckbot',
  'slurp',
  'facebookexternalhit',
  'linkedinbot',
  'twitterbot',
  'whatsapp',
  'telegrambot',
] as const;

/**
 * User-Agent 기반 봇 감지
 */
const isBot = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return BOT_PATTERNS.some((pattern) => userAgent.includes(pattern));
};

/**
 * 이메일 스캐너/클라우드 봇 감지 (특정 뷰포트 패턴)
 * Azure/AWS 이메일 스캐너는 일정한 뷰포트 크기 사용
 */
const SUSPICIOUS_VIEWPORT = {
  width: 1004,
  height: 676,
} as const;

const isSuspiciousViewport = (): boolean => {
  return (
    window.innerWidth === SUSPICIOUS_VIEWPORT.width &&
    window.innerHeight === SUSPICIOUS_VIEWPORT.height
  );
};

const LayoutAuthHandler = () => {
  const { me } = useMeQuery();
  const token = useTokenStore.use.token();
  const setToken = useTokenStore.use.setToken();
  const appLoggedIn = useAppLoggedInStore.use.appLoggedIn();
  const [appIsLoggingIn, setAppIsLoggingIn] = useState(false);
  const history = useHistoryMethod();
  const { pathname, search } = usePathnameMethod();
  const onLoginSuccess = useOnLoginSuccess();
  const datadogLogsInitPromiseRef = useRef<Promise<void> | null>(null);
  const datadogRumInitPromiseRef = useRef<Promise<void> | null>(null);

  /**
   * 로그인해서 토큰이 있는 상태에서 새로고침 했을경우, 혹은 오랜만에 접속 했을경우
   */

  // biome-ignore lint/correctness/useExhaustiveDependencies: 기존 유지
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !me) {
      onLoginSuccess(token, () => {
        history.pushToLogin({ redirectURI: pathname + search });
      });
    }
  }, []);

  /** 데이터독 Logs 초기화 — 초기 번들 크기 절감을 위해 동적 임포트 사용 */
  useEffect(() => {
    datadogLogsInitPromiseRef.current = import('@datadog/browser-logs').then(
      ({ datadogLogs }) => {
        try {
          datadogLogs.init({
            ...COMMON_DATADOG_CONFIG,
            forwardErrorsToLogs: true,
            beforeSend: (event, context) =>
              DatadogLogPolicy.shouldSendLog({ event, context }),
          });
        } catch (error) {
          console.warn('[LayoutAuthHandler] datadogLogs.init 실패:', error);
        }
      },
    );
  }, []);

  /** 데이터독 Logs 사용자 정보 설정 */
  useEffect(() => {
    if (!me) return;
    if (!datadogLogsInitPromiseRef.current) return;

    const setDatadogLogsUser = async () => {
      try {
        await datadogLogsInitPromiseRef.current;

        const { datadogLogs } = await import('@datadog/browser-logs');

        datadogLogs.setUser({
          id: me.id.toString(),
          name: me.name,
          email: me.email,
        });
      } catch (error) {
        console.warn('[LayoutAuthHandler] datadogLogs.setUser 실패:', error);
      }
    };

    setDatadogLogsUser();
  }, [me]);

  /** 데이터독 RUM 초기화 — 초기 번들 크기 절감을 위해 동적 임포트 사용 */
  useEffect(() => {
    datadogRumInitPromiseRef.current = import('@datadog/browser-rum').then(
      ({ datadogRum }) => {
        try {
          datadogRum.init({
            ...COMMON_DATADOG_CONFIG,
            applicationId: 'f9522f5c-7b7a-4c46-8f68-363ec285a1aa',
            sessionSampleRate: RUM_SESSION_SAMPLE_RATE,
            sessionReplaySampleRate: RUM_SESSION_REPLAY_SAMPLE_RATE,
            trackUserInteractions: true,
            trackResources: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
            allowedTracingUrls: ['https://api.uglyus.co.kr'],
            beforeSend: (event) => {
              if (isBot()) return false;
              if (isSuspiciousViewport()) return false;
              // 크로스오리진 등 분석 불가 노이즈 에러 차단 (Logs와 동일 정책 재사용)
              if (
                event.type === 'error' &&
                typeof event.error?.message === 'string' &&
                DatadogLogPolicy.isNoisyMessage(event.error.message)
              ) {
                return false;
              }
              return true;
            },
          });
        } catch (error) {
          console.warn('[LayoutAuthHandler] datadogRum.init 실패:', error);
        }
      },
    );
  }, []);

  /** 데이터독 RUM 사용자 정보 설정 */
  useEffect(() => {
    if (!me) return;
    if (!datadogRumInitPromiseRef.current) return;

    const setDatadogRumUser = async () => {
      try {
        await datadogRumInitPromiseRef.current;

        const { datadogRum } = await import('@datadog/browser-rum');

        datadogRum.setUser({
          id: me.id.toString(),
          name: me.name,
          email: me.email,
        });
      } catch (error) {
        console.warn('[LayoutAuthHandler] datadogRum.setUser 실패:', error);
      }
    };

    setDatadogRumUser();
  }, [me]);

  useEffect(() => {
    if (!('ReactNativeWebView' in window)) return;
    if (!appLoggedIn) return;
    // 앱에서 로그인후 웹뷰 첫 로드시 로컬스토리지에 토큰이 있음에도 recoil 전역 token 이 업데이트 안 되는 이슈
    setAppIsLoggingIn(true);

    let localToken: string | null = null;

    let interval: NodeJS.Timeout;

    const setLocalToken = () => {
      if (!localToken) {
        localToken = localStorage.getItem('token');
        return;
      } else {
        setToken(localToken);
        clearInterval(interval);
      }

      if (token) {
        setAppIsLoggingIn(false);
      }
    };

    interval = setInterval(() => {
      setLocalToken();
    }, 100);

    return () => clearInterval(interval);
  }, [token, appLoggedIn, setToken]);

  if (isReactNative() && appIsLoggingIn && !appLoggedIn && !me) {
    return <LoadingPage />;
  }

  return null;
};

export default LayoutAuthHandler;
